import { LandingHero } from "./LandingHero";
import { LandingInputBar } from "./LandingInputBar";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { ArrowRight, Wand2, Focus, Eye } from "lucide-react";
import {
  trackFunnelLandingView,
  trackFunnelTrialClick,
  trackFunnelLandingInputSubmit,
  trackLandingFirstAction,
  trackLandingCtaClick,
  track,
} from "@/lib/analytics";
import { useExperimentVariant } from "@/lib/abTest";
import { LandingDebugPanel } from "./LandingDebugPanel";
import { Button } from '@/components/ui/button';
import { LandingResultPreview } from './LandingResultPreview';

interface LandingSectionProps {
  onSubmit: (query: string, attachments: any[]) => void;
  isLoading?: boolean;
  onTrySample?: () => void;
}

export default function LandingSection({ onSubmit, isLoading, onTrySample }: LandingSectionProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const navigate = useNavigate();
  // Ref around the helper stack under the input bar. Used by the dev-only
  // debug panel to measure layout density of every direct child.
  const helperStackRef = useRef<HTMLDivElement>(null);
  const copyVariant = useExperimentVariant("landing_trial_copy");
  const placementVariant = useExperimentVariant("landing_cta_placement");
  // Layout-density experiment: "compact" (current calm layout) vs "dense"
  // (prior layout with hint, disclaimer, trial title+sub, tighter gaps).
  const densityVariant = useExperimentVariant("landing_layout_density");
  const isDense = densityVariant === "dense";
  // Focus mode: collapse all helper text under the input and keep
  // only the primary CTA visible. Preference persists across visits.
  const [focusMode, setFocusMode] = useState(false);
  useEffect(() => {
    try {
      setFocusMode(window.localStorage.getItem("atlas-landing-focus-mode") === "1");
    } catch {
      /* Keep the stable default when storage is unavailable. */
    }
  }, []);

  const toggleFocusMode = () => {
    setFocusMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("atlas-landing-focus-mode", next ? "1" : "0");
      } catch {
        /* ignore quota / privacy errors */
      }
      track("landing_focus_mode_toggle", { enabled: next });
      return next;
    });
  };

  // Mount timestamp + first-action latch. Used to compute time-to-first-action
  // per layout-density variant. The latch ensures we emit at most one event
  // per visit so the metric stays a clean per-session timing distribution.
  const mountedAtRef = useRef<number>(typeof performance !== "undefined" ? performance.now() : Date.now());
  const firstActionFiredRef = useRef<boolean>(false);

  const reportFirstAction = (action:
    | "input_focus"
    | "input_type"
    | "attach_open"
    | "cta_click"
    | "signin_click"
    | "try_sample"
    | "submit") => {
    if (firstActionFiredRef.current) return;
    firstActionFiredRef.current = true;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    trackLandingFirstAction(action, now - mountedAtRef.current, densityVariant);
  };

  // Step 1 of funnel: landing page viewed by a guest (LandingSection only mounts for guests)
  useEffect(() => {
    trackFunnelLandingView();
  }, []);

  // Trial copy is always shown under the import bar (including focus mode).
  // Variants only change wording; every variant states the real terms.
  void copyVariant;
  void placementVariant;

  const goToSignup = (source: string) => {
    trackFunnelTrialClick(source);
    trackLandingCtaClick(source, densityVariant);
    reportFirstAction("cta_click");
    navigate(`/sign-in?source=${encodeURIComponent(source)}&tab=signup`);
  };

  // Wrap the parent onSubmit so we can record the funnel step.
  // This is the canonical "guest started the flow" signal we use to
  // compute the landing-submit → guest-modal conversion rate.
  const handleSubmit = (query: string, attachments: any[]) => {
    const fileCount = (attachments || []).filter((a) => a?.type === "file").length;
    const trimmed = (query || "").trim();
    let inputType: "text" | "url" | "youtube" | "files" | "unknown" = "unknown";
    if (fileCount > 0) {
      inputType = "files";
    } else if (trimmed) {
      if (/(?:youtube\.com\/watch|youtu\.be\/)/i.test(trimmed)) inputType = "youtube";
      else if (/^https?:\/\//i.test(trimmed)) inputType = "url";
      else inputType = "text";
    }
    trackFunnelLandingInputSubmit(inputType, fileCount);
    reportFirstAction("submit");
    onSubmit(query, attachments);
  };


  return (
    <div className="atlas-ai-stage relative flex flex-col pt-16 sm:pt-20 overflow-hidden">
      <div className="atlas-ambient-field" aria-hidden="true" />
      {/* Focus mode toggle — top-right of the section. Lets the user collapse
          all helper text under the input and keep only the primary CTA. */}
      <Button variant="ghost" size="icon"
        type="button"
        onClick={toggleFocusMode}
        aria-pressed={focusMode}
        aria-label={focusMode ? t("landing.focusMode.exit", "Exit focus mode") : t("landing.focusMode.enter", "Focus mode")}
        title={focusMode ? t("landing.focusMode.exit", "Exit focus mode") : t("landing.focusMode.enter", "Focus mode")}
        className="absolute top-4 right-4 z-30 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/15 bg-card/65 backdrop-blur-xl text-xs text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors shadow-sm"
      >
        {focusMode ? <Eye className="w-3.5 h-3.5" /> : <Focus className="w-3.5 h-3.5" />}
      </Button>

      <div
        className="relative z-10 container mx-auto px-4 sm:px-6 max-w-3xl"
        // Capture the very first user interaction inside the landing area so
        // we can compute time-to-first-action without instrumenting every
        // child input. `capture: true` (via React's onFocusCapture) ensures
        // we see the event before any child handlers can stop it.
        onFocusCapture={(e) => {
          const tag = (e.target as HTMLElement).tagName;
          if (tag === "INPUT" || tag === "TEXTAREA") reportFirstAction("input_focus");
        }}
        onKeyDownCapture={(e) => {
          const tag = (e.target as HTMLElement).tagName;
          if (tag === "INPUT" || tag === "TEXTAREA") reportFirstAction("input_type");
        }}
      >
        <LandingHero />
        <LandingInputBar onSubmit={handleSubmit} isLoading={isLoading ?? false} />

        {/* Vertical stack under the input. Using `flex-col` + `gap` instead of
            ad-hoc `mt-*` on each child guarantees consistent spacing on every
            viewport, so children never collide on narrow phones even when
            translated strings wrap onto two lines.
            Bottom padding leaves room for the floating Focus toggle that sits
            in the top-right of the section on mobile. */}
        <div
          ref={helperStackRef}
          className={
            isDense
              // Dense: tighter mt + smaller gaps to mirror the prior layout.
              ? "mt-3 flex flex-col items-center gap-2 max-w-2xl mx-auto pb-16 sm:pb-0"
              : "mt-5 sm:mt-6 flex flex-col items-center gap-5 sm:gap-6 max-w-2xl mx-auto pb-16 sm:pb-0"
          }
        >
          {/* Dense variant: restore the formats hint + AI disclaimer that the
              compact layout strips out. Kept inside the experiment branch so
              it never ships unintentionally to the compact treatment. */}
          {isDense && !focusMode && (
            <>
              <p className="text-center text-xs text-muted-foreground leading-snug">
                {t("landing.inputBar.hint", "PDF, Word, PowerPoint, YouTube, or just describe your topic")}
              </p>
              <p className="text-center text-[11px] text-muted-foreground leading-snug">
                {t("landing.inputBar.disclaimer", "Atlas can make mistakes. Always verify important information.")}
              </p>
            </>
          )}

          {/* Discreet "Try with a sample" action — instantly generates a system
              reading grid from a built-in cell-biology excerpt, no account needed. */}
          {onTrySample && !focusMode && (
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.35 }}
              className="w-full flex items-center justify-center"
            >
              <Button variant="ghost" size="sm"
                type="button"
                disabled={isLoading}
                onClick={() => {
                  track("landing_try_sample_click", { source: "landing_below_input" });
                  reportFirstAction("try_sample");
                  onTrySample();
                }}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/80 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-center px-3 py-1 leading-snug"
              >
                <Wand2 className="w-3.5 h-3.5 shrink-0" />
                <span className="whitespace-normal">{t("landing.trySample", "Try with a sample. Cell Biology")}</span>
              </Button>
            </motion.div>
          )}

          {/* Inline CTA variant — visible right under the input bar.
              In focus mode this remains the single primary action.
              In the dense layout-density variant we also pre-render the
              trial title/sub block above the CTA to mirror the prior
              layout, regardless of the trial-copy A/B. */}
          {(
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="w-full flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4"
            >
              {(
                <div className="text-center sm:text-left leading-tight max-w-md">
                  <p className="text-sm font-semibold text-foreground">
                    {t("freeTrialBadge.title", "Try Pro free for 7 days")}
                  </p>
                  <p className="text-xs text-muted-foreground whitespace-normal">
                    {t("freeTrialBadge.subtitle", "Payment method required for the Pro trial.")}
                  </p>
                </div>
              )}
              <Button
                onClick={() =>
                  goToSignup(
                    focusMode
                      ? "focus_mode_cta"
                      : isDense
                        ? "density_dense_cta"
                        : "density_compact_cta",
                  )
                }
                className={
                  isDense
                    ? "w-full sm:w-auto max-w-xs px-5 py-3 h-auto text-center leading-snug"
                    : "w-full sm:w-auto max-w-xs px-6 py-3 h-auto text-center leading-snug"
                }
              >
                <span>{t("freeTrialBadge.cta", "Start your free trial")}</span>
                <ArrowRight className="w-4 h-4 shrink-0" />
              </Button>
            </motion.div>
          )}

          {/* Single discrete sign-in link for returning users.
              `flex-wrap` lets long translations break to a second line
              instead of overflowing the viewport on small screens. */}
          {!focusMode && (
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.4 }}
              className="w-full flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-xs text-muted-foreground px-2"
            >
              <span>{t("landing.haveAccount", "Already have an account?")}</span>
              <Button variant="link" size="sm"
                onClick={() => {
                  reportFirstAction("signin_click");
                  navigate("/sign-in");
                }}
                className="font-semibold text-primary hover:underline underline-offset-2 transition-colors"
              >
                {t("landing.signIn", "Sign in")}
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      {!focusMode && <LandingResultPreview />}
      {/* Dev-only layout debug panel. Tree-shaken in production builds. */}
      {import.meta.env.DEV && <LandingDebugPanel targetRef={helperStackRef as React.RefObject<HTMLElement>} />}
    </div>
  );
}
